import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { getSessionId } from '@fs/zion-session'
import { useEvent } from '@fs/zion-frontend-friends'
import { useStatusOverlay } from '@fs/zion-ui'
import { type AxiosError } from '@fs/zion-axios'
import { compareTree, deleteTree, useTrees } from '../../lib/my-trees-service'

export type GenealogyTree = {
  id: number | null
  treeUri: string
  treeType: string | null
  name: string
  description: string | null
  cisId: string
  treeStatus: string
  errorDetail: string | null
  createDate: string
  completeDate: string | null
  downloadUri: string
  fileSize: number
  personCount: number
  communityTree: boolean
  prfTree: boolean
  collectionTitle: string | null
  compareSummary: unknown | null
  statistics: unknown | null
}

type UseGenealogyTreesReturn = {
  trees: GenealogyTree[] | undefined
  error: AxiosError | null
  beingDeleted: boolean
  handleCompare: (treeUri: string) => void
  handleDelete: (treeUri: string) => Promise<void>
  refetch: () => void
}

export default function useGenealogyTrees(): UseGenealogyTreesReturn {
  const [t] = useTranslation()

  const sessionId = getSessionId()
  const [beingDeleted, setBeingDeleted] = useState(false)
  const showStatusOverlay = useStatusOverlay()
  const [{ data: trees, error }, refetch] = useTrees()

  const handleDelete = useEvent(async (treeUri: string) => {
    setBeingDeleted(true)

    try {
      await deleteTree(sessionId, treeUri)
      refetch()
    } catch (err) {
      console.error(err)
      showStatusOverlay({
        type: 'error',
        message: t('tree-import.upload-trees.error.delete_error'),
      })
    } finally {
      setBeingDeleted(false)
    }
  })

  const handleCompare = useEvent((treeUri: string) => {
    compareTree(sessionId, treeUri)
      .then(() => {
        refetch()
        return undefined
      })
      .catch((err) => {
        console.error(err)
        showStatusOverlay({
          type: 'error',
          message: t('tree-import.upload-trees.error.compare_queue_connection'),
        })
      })
  })

  return { trees, error, beingDeleted, handleCompare, handleDelete, refetch }
}
